import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions
} from '@tanstack/react-query';
import { searchApi } from './search.api';
import type { SearchParams } from '../types';

export const searchKeys = {
  all: ['search'] as const,
  query: (params: SearchParams) =>
    [...searchKeys.all, 'query', params] as const,
  infinite: (params: Omit<SearchParams, 'page'>) =>
    [...searchKeys.all, 'infinite', params] as const
};

export const searchQueries = {
  /**
   * Single-page search. Backs the Cmd+K dialog, which shows a small fixed set.
   * `enabled` is set by the consuming hook so the caller can hold off firing
   * while the user is still typing.
   */
  query: (params: SearchParams, enabled = true) =>
    queryOptions({
      queryKey: searchKeys.query(params),
      queryFn: () => searchApi.query(params),
      enabled,
      placeholderData: keepPreviousData,
      staleTime: 30_000
    }),

  /**
   * Paginated search for the full results page (infinite scroll). Advances by
   * page until the loaded count reaches the reported total. The interpretation
   * / degraded flags are identical across pages, so the view reads them off the
   * first page.
   */
  infinite: (params: Omit<SearchParams, 'page'>, enabled = true) =>
    infiniteQueryOptions({
      queryKey: searchKeys.infinite(params),
      queryFn: ({ pageParam }) =>
        searchApi.query({ ...params, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.page * lastPage.per_page < lastPage.total
          ? lastPage.page + 1
          : undefined,
      enabled,
      staleTime: 30_000
    })
};
